(() => {
  if (customElements.get('pe-customer-reviews')) return;
  const requests = new Map();
  function loadReviews(url) {
    if (!requests.has(url)) {
      requests.set(url, fetch(url).then(response => {
        if (!response.ok) throw new Error('Reviews unavailable');
        return response.json();
      }).then(data => {
        if (!Array.isArray(data.reviews)) throw new Error('Invalid review data');
        return data.reviews.filter(review => Number.isInteger(review.rating) &&
          review.rating >= 1 && review.rating <= 5 && typeof review.body === 'string' && review.body.trim());
      }).catch(error => { requests.delete(url); throw error; }));
    }
    return requests.get(url);
  }
  function photoUrls(review) {
    return (Array.isArray(review.photo_urls) ? review.photo_urls : []).filter(source => {
      try { return new URL(source).protocol === 'https:'; } catch { return false; }
    }).slice(0, 6);
  }
  function compareReviews(a, b, order) {
    if (order === 'photos') {
      const difference = Number(photoUrls(b).length > 0) - Number(photoUrls(a).length > 0);
      if (difference) return difference;
    }
    return String(b.created_at || '').localeCompare(String(a.created_at || ''));
  }
  class CustomerReviews extends HTMLElement {
    async connectedCallback() {
      if (this.started) return;
      this.started = true;
      // Full sections show the complete catalogue review collection with product links.
      // Keep the compact cart drawer specific to the product named above its card.
      if (!this.classList.contains('pe-drawer-reviews')) this.dataset.product = '';
      this.list = this.querySelector('[data-reviews-list]');
      this.status = this.querySelector('[data-reviews-status]');
      this.more = this.querySelector('[data-reviews-more]');
      this.more.addEventListener('click', () => this.showNext());
      try {
        const reviews = await loadReviews(this.dataset.source);
        this.reviews = reviews.filter(review =>
          (!this.dataset.product || review.product_handle === this.dataset.product)
        ).sort((a, b) => compareReviews(a, b, 'photos'));
        if (!this.reviews.length) {
          if (this.dataset.editor === 'true') {
            this.status.textContent = 'No imported reviews for this product. This section is hidden on the storefront.';
          } else {
            this.hidden = true;
          }
          return;
        }
        const total = this.reviews.length;
        const average = (this.reviews.reduce((sum, review) => sum + review.rating, 0) / total).toFixed(1);
        const summary = this.querySelector('[data-reviews-summary]');
        summary.textContent = `${average} / 5 · ${total} ${total === 1 ? 'review' : 'reviews'}${this.dataset.product ? '' : ' across products'}`;
        const score = this.querySelector('[data-review-score]');
        if (score) score.textContent = average;
        const bars = this.querySelector('[data-review-bars]');
        if (bars) {
          bars.replaceChildren();
          for (let rating = 5; rating >= 1; rating--) {
            const count = this.reviews.filter(review => review.rating === rating).length;
            const row = this.node('div', 'pe-rating-row');
            row.append(this.node('span', '', `${rating} star`));
            const meter = this.node('meter', '');
            meter.min = 0; meter.max = total; meter.value = count;
            meter.setAttribute('aria-label', `${rating} stars: ${count} reviews`);
            row.append(meter, this.node('span', '', `${Math.round(count / total * 100)}%`));
            bars.append(row);
          }
        }
        const sort = this.querySelector('[data-review-sort]');
        if (sort) sort.addEventListener('change', () => {
          this.reviews.sort((a, b) => compareReviews(a, b, sort.value));
          this.list.replaceChildren();
          this.shown = 0;
          this.showNext();
        });
        this.shown = 0;
        this.showNext();
      } catch (error) {
        this.status.textContent = 'Reviews could not be loaded. Please refresh the page to try again.';
      }
    }
    node(tag, className, text) {
      const element = document.createElement(tag);
      if (className) element.className = className;
      if (text !== undefined) element.textContent = text;
      return element;
    }
    card(review) {
      const card = this.node('article', 'pe-review-card');
      const stars = this.node('span', 'pe-review-stars');
      for (let index = 1; index <= 5; index++) {
        const tile = this.node('span', index <= review.rating ? 'pe-star-tile is-filled' : 'pe-star-tile', '★');
        tile.style.cssText = `display:inline-grid;place-items:center;width:22px;height:22px;margin-right:3px;background:${index <= review.rating ? '#24634a' : '#d9ddd9'};color:white;font-size:17px;line-height:1`;
        tile.setAttribute('aria-hidden', 'true');
        stars.append(tile);
      }
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', `${review.rating} out of 5 stars`);
      card.append(stars);
      if (review.title) card.append(this.node('h3', '', review.title));
      card.append(this.node('p', 'pe-review-body', review.body));
      // Only explicitly supplied, corrected photo associations belong in the public data.
      if (photoUrls(review).length) {
        const photos = this.node('div', 'pe-review-photos');
        photos.style.cssText = 'display:flex;flex-wrap:wrap;gap:8px;margin:8px 0';
        for (const [index, source] of photoUrls(review).entries()) {
          let url;
          try { url = new URL(source); } catch { continue; }
          if (url.protocol !== 'https:') continue;
          const link = this.node('a', 'pe-review-photo');
          link.href = url.href;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          link.setAttribute('aria-label', `Open review photo ${index + 1} in a new tab`);
          const photo = this.node('img', '');
          photo.alt = `Photo attached to ${review.author}'s review of ${review.product_title}`;
          photo.loading = 'lazy';
          photo.width = 96;
          photo.height = 96;
          photo.style.cssText = 'display:block;width:96px;height:96px;object-fit:contain;background:#fff;border:1px solid #deded8';
          photo.addEventListener('error', () => link.remove(), { once: true });
          photo.src = url.href;
          link.append(photo);
          photos.append(link);
        }
        if (photos.children.length) card.append(photos);
      }
      const credit = this.node('div', 'pe-review-credit');
      credit.append(this.node('span', '', review.author));
      const date = this.node('time', '', review.created_at);
      date.dateTime = review.created_at;
      credit.append(date);
      card.append(credit);
      if (!this.dataset.product) {
        const link = this.node('a', 'pe-review-product', review.product_title);
        const root = (this.dataset.root || '/').replace(/\/$/, '');
        link.href = `${root}/products/${encodeURIComponent(review.product_handle)}`;
        card.append(link);
      }
      if (review.reply) {
        const reply = this.node('div', 'pe-review-reply');
        reply.append(this.node('strong', '', 'Response from PAIR EDIT'));
        reply.append(this.node('p', '', review.reply));
        card.append(reply);
      }
      return card;
    }
    showNext() {
      const pageSize = Math.max(1, Math.min(12, Number(this.dataset.pageSize) || 3));
      const end = Math.min(this.shown + pageSize, this.reviews.length);
      const firstNew = this.shown;
      for (const review of this.reviews.slice(this.shown, end)) this.list.append(this.card(review));
      this.shown = end;
      this.status.textContent = `Showing ${end} of ${this.reviews.length} ${this.reviews.length === 1 ? 'review' : 'reviews'}.`;
      this.more.hidden = end >= this.reviews.length;
      if (firstNew > 0) {
        const card = this.list.children[firstNew];
        card.tabIndex = -1;
        card.focus({ preventScroll: true });
      }
    }
  }
  customElements.define('pe-customer-reviews', CustomerReviews);
  class ProductRating extends HTMLElement {
    async connectedCallback() {
      if (this.started) return;
      this.started = true;
      try {
        const reviews = (await loadReviews(this.dataset.source)).filter(review => review.product_handle === this.dataset.product);
        if (!reviews.length) return;
        const average = reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length;
        this.querySelector('[data-product-rating-fill]').style.width = `${average / 5 * 100}%`;
        this.querySelector('[data-product-rating-text]').textContent = `${average.toFixed(1)} / 5 · ${reviews.length} ${reviews.length === 1 ? 'review' : 'reviews'} for this product`;
        this.hidden = false;
      } catch { this.hidden = true; }
    }
  }
  if (!customElements.get('pe-product-rating')) customElements.define('pe-product-rating', ProductRating);
})();
